import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

const handleImageError = (error: Error, context: string): Error => {
  if (isDevOrTestStage()) {
    throw error;
  } else {
    reportErrorToBugsnag({ error, context });
  }

  return error;
};

const KIBIBYTE = 1024;
const MEBIBYTE = KIBIBYTE * KIBIBYTE;
const MAX_IMAGE_DIMENSION = 4096;
const MAX_BASE64_SIZE = 2 * MEBIBYTE;
const MIN_DIMENSION = 512;
const INITIAL_QUALITY = 0.9;
const QUALITY_STEP = 0.1;
const DIMENSION_SCALE = 0.8;

const IMAGE_SEARCH_DROPZONE_ACCEPT_BY_MIME: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/jpg': ['.jpg'],
  'image/png': ['.png'],
};

export const ACCEPTED_MIME_TYPES = Object.keys(IMAGE_SEARCH_DROPZONE_ACCEPT_BY_MIME);

export const IMAGE_SEARCH_DROPZONE_ACCEPT = IMAGE_SEARCH_DROPZONE_ACCEPT_BY_MIME;

export const isAcceptedImageSearchMimeType = (mimeType: string) => ACCEPTED_MIME_TYPES.includes(mimeType.toLowerCase());

const shouldKeepCompressingBase64 = (base64: string, quality: number, width: number, maxBytes: number) =>
  base64.length > maxBytes && (quality > QUALITY_STEP || width > MIN_DIMENSION);

const getOptimizedBase64 = (sourceCanvas: HTMLCanvasElement, maxBytes: number): string => {
  let currentCanvas = sourceCanvas;
  let quality = INITIAL_QUALITY;
  let base64 = currentCanvas.toDataURL('image/jpeg', quality);

  while (shouldKeepCompressingBase64(base64, quality, currentCanvas.width, maxBytes)) {
    if (currentCanvas.width > MIN_DIMENSION) {
      const newCanvas = document.createElement('canvas');
      newCanvas.width = Math.floor(currentCanvas.width * DIMENSION_SCALE);
      newCanvas.height = Math.floor(currentCanvas.height * DIMENSION_SCALE);

      const ctx = newCanvas.getContext('2d');

      if (!ctx) {
        return currentCanvas.toDataURL('image/jpeg', quality);
      }

      ctx.drawImage(currentCanvas, 0, 0, newCanvas.width, newCanvas.height);
      currentCanvas = newCanvas;
    } else {
      quality -= QUALITY_STEP;
    }

    base64 = currentCanvas.toDataURL('image/jpeg', quality);
  }

  return base64;
};

const processImageToBase64 = (img: HTMLImageElement): string => {
  const canvas = document.createElement('canvas');
  let { width, height } = img;

  if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
    const scale = MAX_IMAGE_DIMENSION / Math.max(width, height);
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
  }

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw handleImageError(new Error('Could not get canvas context'), 'processImageToBase64: Failed to get 2D context');
  }

  ctx.drawImage(img, 0, 0, width, height);

  return getOptimizedBase64(canvas, MAX_BASE64_SIZE);
};

export const resizeImageToBase64 = async (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;

      if (!result || typeof result !== 'string') {
        reject(
          handleImageError(
            new Error('Failed to read file as data URL'),
            'resizeImageToBase64: Invalid FileReader result',
          ),
        );
        return;
      }

      const img = new Image();

      img.onload = () => {
        try {
          const base64 = processImageToBase64(img);
          resolve(base64);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(handleImageError(new Error('Failed to load image'), 'resizeImageToBase64: Image load failed'));
      };

      img.src = result;
    };

    reader.onerror = () => {
      reject(handleImageError(new Error('Failed to read file'), 'resizeImageToBase64: FileReader error'));
    };

    reader.readAsDataURL(file);
  });

export const downloadImageAndConvertToBase64 = async (imageUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        const base64 = processImageToBase64(img);
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(
        handleImageError(
          new Error('Failed to load image from URL'),
          'downloadImageAndConvertToBase64: Image load failed',
        ),
      );
    };

    img.src = imageUrl;
  });
